import { useEffect, useMemo, useState } from "react";
import { Api, ApiError, type AgentDetail, type Me, type ModelRegistry, type SignupMode } from "./api.js";
import { AgentList } from "./components/AgentList.js";
import { Editor, STEPS, type StepId } from "./components/Editor.js";
import { Keys } from "./components/Keys.js";
import { LeadDetail, Leads } from "./components/Leads.js";
import { Login } from "./components/Login.js";
import { AnalyticsNav, AnalyticsScreen, parseAnalyticsRoute, type AnalyticsRoute } from "./analytics/Analytics.js";
import { AcceptInviteScreen, DownloadExportScreen, ExportRequestScreen, CheckEmailScreen, ForgotScreen, LoginScreen, ResetScreen, SignupScreen, VerifyScreen } from "./auth/Screens.js";
import { MfaChallenge, MfaSetupScreen } from "./auth/Mfa.js";
import { AccountPage } from "./components/AccountPage.js";
import { ActivityPage } from "./components/Activity.js";
import { AccountSwitcher } from "./components/AccountSwitcher.js";
import { Onboarding } from "./components/Onboarding.js";
import { Team } from "./components/Team.js";
import { AgentSharing } from "./components/Sharing.js";
import { AcceptShareScreen, AcceptTransferScreen } from "./auth/Sharing.js";
import { I18nProvider, initialLang, rememberLang, useI18n, type Lang } from "./i18n.js";
import { ModelMigrationBanner } from "./components/ModelMigrationBanner.js";
import { Notifications, useNotificationCount } from "./components/Notifications.js";
import { Billing, BillingBanner, useBillingSummary } from "./components/Billing.js";
import { intentFromQuery, rememberIntent, takeIntent, type BillingIntent } from "./billingIntent.js";
import { ApiKeyCredentials, SessionCredentials, type CredentialSource } from "./credentials.js";
import { runtimeConfig } from "./runtime-config.js";

type Route =
  | { name: "agents" }
  | { name: "leads" }
  | { name: "lead"; id: number }
  | { name: "keys" }
  | { name: "notifications" }
  | { name: "team" }
  | { name: "account" }
  | { name: "share"; id: string }
  | { name: "transfer"; token?: string; id?: string }
  | { name: "billing"; intent: BillingIntent | null }
  | AnalyticsRoute
  | { name: "activity"; id: string; tab: "activity" | "versions" }
  | { name: "agent"; id: string; step: StepId };

/** Screens that exist before anyone is signed in. The token of an e-mailed link rides in the `#` part, so it never reaches a server log. */
export type AuthRoute = { name: "export"; token: string } | { name: "download-export"; token: string } | { name: "login" } | { name: "signup"; invite?: string } | { name: "forgot" } | { name: "reset"; token: string } | { name: "verify"; token: string } | { name: "invite"; token: string } | { name: "share"; token: string };

export function parseAuthRoute(hash: string): AuthRoute | null {
  const [path = "", query = ""] = hash.replace(/^#/, "").split("?");
  const token = new URLSearchParams(query).get("token") ?? "";
  if (path === "/export" && token) return { name: "export", token };
  if (path === "/download-export" && token) return { name: "download-export", token };
  if (path === "/login") return { name: "login" };
  if (path === "/signup") {
    const invite = new URLSearchParams(query).get("invite");
    return invite ? { name: "signup", invite } : { name: "signup" };
  }
  if (path === "/forgot") return { name: "forgot" };
  if (path === "/reset-password" && token) return { name: "reset", token };
  if (path === "/verify-email" && token) return { name: "verify", token };
  if (path === "/accept-invite" && token) return { name: "invite", token };
  if (path === "/accept-share" && token) return { name: "share", token };
  return null;
}

export function parseRoute(hash: string): Route {
  const [path = "", query = ""] = hash.replace(/^#/, "").split("?");
  const sh = /^\/agents\/([\w-]+)\/share$/.exec(path);
  if (sh) return { name: "share", id: sh[1] as string };
  const tr = /^\/transfers\/([\w-]+)$/.exec(path);
  if (tr) return { name: "transfer", id: tr[1] as string };
  if (path === "/accept-transfer") {
    const token = new URLSearchParams(query).get("token");
    if (token) return { name: "transfer", token };
  }
  const h = /^\/agents\/([\w-]+)\/(activity|versions)$/.exec(path);
  if (h) return { name: "activity", id: h[1] as string, tab: h[2] as "activity" | "versions" };
  const m = /^\/agents\/([\w-]+)$/.exec(path);
  if (m) {
    const s = new URLSearchParams(query).get("step") as StepId | null;
    return { name: "agent", id: m[1] as string, step: s && STEPS.includes(s) ? s : "plan" };
  }
  const l = /^\/leads\/(\d+)$/.exec(path);
  if (l) return { name: "lead", id: Number(l[1]) };
  if (path === "/leads") return { name: "leads" };
  if (path === "/keys") return { name: "keys" };
  if (path === "/team") return { name: "team" };
  if (path === "/account") return { name: "account" };
  if (path === "/billing") return { name: "billing", intent: intentFromQuery(query) };
  if (path === "/notifications") return { name: "notifications" };
  const analytics = parseAnalyticsRoute(path);
  if (analytics) return analytics;
  return { name: "agents" };
}

function useRoute() {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(location.hash));
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}

/**
 * Three ways in, tried in this order:
 *  1. a person's account (cookie session): sign-up, login, teams. Used whenever the API has sign-in turned on;
 *  2. an API key (the original way), offered as a small link under the login, and as the only way when the API has
 *     sign-in turned off or an `ApiKeyCredentials` is handed in (tests, embedded setups).
 */
export function App({ credentials: injected, apiUrl }: { credentials?: ApiKeyCredentials; apiUrl?: string }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const setLang = (l: Lang) => {
    rememberLang(l);
    setLangState(l);
  };
  return (
    <I18nProvider lang={lang} setLang={setLang}>
      {injected ? <KeyApp credentials={injected} apiUrl={apiUrl} /> : <AccountApp apiUrl={apiUrl} />}
    </I18nProvider>
  );
}

type Mode = { name: "boot" } | { name: "auth"; notice?: string } | { name: "session"; me: Me } | { name: "key" } | { name: "check-email"; email: string };

function AccountApp({ apiUrl }: { apiUrl?: string }) {
  const { t, lang, setLang } = useI18n();
  const base = apiUrl ?? runtimeConfig().apiUrl;
  const session = useMemo(() => new SessionCredentials(), []);
  const [mode, setMode] = useState<Mode>({ name: "boot" });
  const [google, setGoogle] = useState(false);
  const [signupMode, setSignupMode] = useState<SignupMode>("open");
  const [signupDomains, setSignupDomains] = useState<string[]>([]);
  const authRoute = useAuthRoute();
  // A marketing link such as #/signup?plan=explorer&interval=year: remember the plan until the person is signed in.
  useEffect(() => {
    const [path = "", query = ""] = location.hash.replace(/^#/, "").split("?");
    if (path === "/signup" || path === "/login") {
      const i = intentFromQuery(query);
      if (i) rememberIntent(i);
    }
  }, [authRoute]);
  const keyCredentials = useMemo(() => new ApiKeyCredentials(), []);

  const api = useMemo(
    () =>
      new Api(base, session, undefined, () => {
        session.signOut();
        setMode({ name: "auth", notice: t("login.sessionEnded") });
      }),
    // the message language at the time of the first 401 is good enough; the notice is re-read on render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, session],
  );

  const enter = (me: Me) => {
    session.set(me.csrfToken, me.activeAccountId);
    // the person's own language wins over the browser's once they have signed in
    if (me.user.language !== lang) setLang(me.user.language);
    setMode({ name: "session", me });
    // A plan chosen on the website: an owner lands on Billing with it selected; they confirm there before anything is charged.
    const intent = me.accounts.find((a) => a.id === me.activeAccountId)?.role === "owner" && !me.mfa.blocked ? takeIntent() : null;
    if (intent?.plan) location.hash = `#/billing?plan=${encodeURIComponent(intent.plan)}${intent.interval ? `&interval=${intent.interval}` : ""}`;
    else {
      const here = parseAuthRoute(location.hash);
      if (here && here.name !== "export" && here.name !== "download-export" && here.name !== "invite") location.hash = "#/";
    }
  };

  useEffect(() => {
    let live = true;
    api.providers().then((p) => { if (live) { setGoogle(p.google); setSignupMode(p.signup ?? "open"); setSignupDomains(p.signupDomains ?? []); } }, () => undefined);
    api.me().then(
      (me) => live && enter(me),
      (e) => {
        if (!live) return;
        // 401: nobody signed in. 404: this deployment has no sign-up yet, so the API key is the way in.
        if (e instanceof ApiError && e.status === 401) setMode({ name: "auth" });
        else setMode({ name: "key" });
      },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  if (mode.name === "boot") return <main className="page" id="main"><p role="status">{t("common.loading")}</p></main>;
  // The deletion e-mails' links work for anyone holding them, signed in or not.
  if (authRoute?.name === "export") return <ExportRequestScreen api={api} token={authRoute.token} />;
  if (authRoute?.name === "download-export") return <DownloadExportScreen api={api} token={authRoute.token} />;
  if (mode.name === "key") return <KeyApp credentials={keyCredentials} apiUrl={apiUrl} />;

  if (mode.name === "session") {
    return (
      <SessionShell
        api={api}
        session={session}
        me={mode.me}
        setMe={(me) => {
          session.setAccount(me.activeAccountId);
          setMode({ name: "session", me });
        }}
        onSignedOut={() => {
          session.signOut();
          setMode({ name: "auth" });
          location.hash = "#/login";
        }}
      />
    );
  }

  const googleError = new URLSearchParams(location.hash.split("?")[1] ?? "").get("error");
  const notice = mode.name === "auth" ? (mode.notice ?? (googleError === "google_failed" ? t("login.googleFailed") : googleError === "not_staff" ? t("login.notStaff") : googleError === "signup_closed" ? t("login.signupClosed") : null)) : null;
  // The e-mailed link opens in a new tab in real life; if it opens here, it wins over the "check your email" page.
  if (mode.name === "check-email" && !(authRoute && ["verify", "reset", "invite", "share"].includes(authRoute.name))) return <CheckEmailScreen api={api} email={mode.email} />;
  switch (authRoute?.name) {
    case "signup":
      return <SignupScreen api={api} google={google} mode={signupMode} domains={signupDomains} invite={authRoute.invite} onDone={(email) => setMode({ name: "check-email", email })} onSignedIn={enter} />;
    case "forgot":
      return <ForgotScreen api={api} />;
    case "reset":
      return <ResetScreen api={api} token={authRoute.token} />;
    case "verify":
      return <VerifyScreen api={api} token={authRoute.token} onSignedIn={enter} />;
    case "invite":
      return <AcceptInviteScreen api={api} token={authRoute.token} me={null} onJoined={(m) => { enter(m); location.hash = "#/"; }} />;
    case "share":
      return <AcceptShareScreen api={api} token={authRoute.token} me={null} onJoined={(agentId, me) => { if (me) enter(me); location.hash = `#/agents/${agentId}`; }} />;
    default:
      return <LoginScreen api={api} google={google} canSignUp={signupMode !== "closed"} notice={notice} onSignedIn={enter} onApiKey={() => setMode({ name: "key" })} />;
  }
}

function useAuthRoute(): AuthRoute | null {
  const [r, setR] = useState<AuthRoute | null>(() => parseAuthRoute(location.hash));
  useEffect(() => {
    const on = () => setR(parseAuthRoute(location.hash));
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return r;
}

/** The original sign-in with an API key. Behaves exactly as before. */
function KeyApp({ credentials: injected, apiUrl }: { credentials: ApiKeyCredentials; apiUrl?: string }) {
  const credentials = injected;
  const [signedIn, setSignedIn] = useState(!!credentials.authorization());
  const [loginError, setLoginError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [models, setModels] = useState<ModelRegistry | null>(null);
  const route = useRoute();

  const api = useMemo(
    () =>
      new Api(apiUrl ?? runtimeConfig().apiUrl, credentials, undefined, () => {
        credentials.signOut();
        setSignedIn(false);
        setLoginError("Your session ended. Sign in again; your latest changes were saved.");
      }),
    [credentials, apiUrl],
  );

  useEffect(() => {
    if (!signedIn) return;
    api.models().then(setModels, () => setModels(null));
  }, [api, signedIn]);

  const alerts = useNotificationCount(api, signedIn, route.name);

  const signIn = async (key: string, remember: boolean) => {
    setChecking(true);
    setLoginError(null);
    credentials.signIn(key, remember);
    try {
      setModels(await api.models());
      setSignedIn(true);
    } catch (e) {
      credentials.signOut();
      setLoginError(e instanceof ApiError && e.code !== "network" ? "That API key is not valid or was revoked." : e instanceof ApiError ? e.message : "Could not sign in.");
    } finally {
      setChecking(false);
    }
  };

  if (!signedIn) return <Login onSubmit={signIn} error={loginError} busy={checking} />;

  return (
    <div className="app">
      <header className="topbar">
        <a href="#/" className="brand">
          Nemo
        </a>
        <MainNav route={route} alerts={alerts} />
        <button
          className="btn ghost"
          onClick={() => {
            credentials.signOut();
            setSignedIn(false);
            location.hash = "#/";
          }}
        >
          Sign out
        </button>
      </header>
      <Routes api={api} route={route} models={models} />
    </div>
  );
}

function MainNav({ route, alerts, team }: { route: Route; alerts: number; team?: { team: string; account: string; keys: boolean; billing?: string } }) {
  return (
    <nav aria-label="Main">
      <a href="#/" aria-current={route.name === "agents" || route.name === "agent" ? "page" : undefined}>
        Agents
      </a>
      <a href="#/leads" aria-current={route.name === "leads" || route.name === "lead" ? "page" : undefined}>
        Leads
      </a>
      <AnalyticsNav route={route} />
      <a href="#/notifications" aria-current={route.name === "notifications" ? "page" : undefined}>
        Notifications
        {alerts > 0 && (
          <span className="count" aria-label={`${alerts} problem${alerts === 1 ? "" : "s"} in the last day`}>
            {alerts}
          </span>
        )}
      </a>
      {(!team || team.keys) && (
        <a href="#/keys" aria-current={route.name === "keys" ? "page" : undefined}>
          API keys
        </a>
      )}
      {team && (
        <>
          <a href="#/team" aria-current={route.name === "team" ? "page" : undefined}>
            {team.team}
          </a>
          {team.billing && (
            <a href="#/billing" aria-current={route.name === "billing" ? "page" : undefined}>
              {team.billing}
            </a>
          )}
          <a href="#/account" aria-current={route.name === "account" ? "page" : undefined}>
            {team.account}
          </a>
        </>
      )}
    </nav>
  );
}

function Routes({ api, route, models, keysNote }: { api: Api; route: Route; models: ModelRegistry | null; keysNote?: string }) {
  return (
    <>
      {route.name === "agents" && <AgentList api={api} onOpen={(id) => (location.hash = `#/agents/${id}`)} />}
      {route.name === "leads" && <Leads api={api} />}
      {route.name === "lead" && <LeadDetail key={route.id} api={api} id={route.id} />}
      {(route.name === "statistics" || route.name === "conversations" || route.name === "conversation") && <AnalyticsScreen api={api} route={route} />}
      {route.name === "keys" && <Keys api={api} note={keysNote} />}
      {route.name === "notifications" && <Notifications api={api} />}
      {route.name === "activity" && <ActivityPage key={route.id} api={api} agentId={route.id} tab={route.tab} />}
      {route.name === "agent" && <AgentScreen key={route.id} api={api} id={route.id} step={route.step} models={models} />}
    </>
  );
}

/** A signed-in person: header with account switcher, team and account pages, onboarding for an empty account. */
function SessionShell({ api, session, me, setMe, onSignedOut }: { api: Api; session: SessionCredentials; me: Me; setMe: (me: Me) => void; onSignedOut: () => void }) {
  const { t } = useI18n();
  const route = useRoute();
  const [models, setModels] = useState<ModelRegistry | null>(null);
  const [agentCount, setAgentCount] = useState<number | null>(null);
  const active = me.accounts.find((a) => a.id === me.activeAccountId) ?? null;
  const role = active?.role ?? "member";
  const signOut = () => void api.logout().then(onSignedOut, onSignedOut);
  const alerts = useNotificationCount(api, !!active, route.name);
  const billing = useBillingSummary(api, !!active && role === "owner", `${me.activeAccountId}:${route.name}`);

  useEffect(() => {
    if (!active) return;
    setModels(null);
    setAgentCount(null);
    api.models().then(setModels, () => setModels(null));
    // Agents shared with this person count: someone who only has shared agents must not be sent to onboarding.
    // An ownership offer waiting for this person counts too: its banner lives on the agent list.
    Promise.all([api.listAllAgents(), api.transfers().catch(() => ({ incoming: [] as unknown[] }))]).then(
      ([l, t]) => setAgentCount(l.agents.length + l.shared.length + (t.incoming?.length ?? 0)),
      () => setAgentCount(null),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, me.activeAccountId]);

  const switchTo = async (id: string) => {
    session.setAccount(id);
    setMe(await api.switchAccount(id));
    location.hash = "#/";
  };

  // Password (or Google) done, second step not: nothing else works yet, so only the code screen is shown.
  if (me.mfa.blocked) {
    const refresh = () => void api.me().then(setMe, onSignedOut);
    return me.mfa.enrolled ? <MfaChallenge api={api} me={me} onDone={setMe} onSignOut={signOut} /> : <MfaSetupScreen api={api} onDone={refresh} onSignOut={signOut} />;
  }

  if (!active) {
    // Every account the person had was deleted or they left it: they can start another.
    return (
      <div className="app">
        <header className="topbar">
          <span className="brand">Nemo</span>
          <button className="btn ghost" onClick={signOut}>
            {t("nav.signout")}
          </button>
        </header>
        <main className="page" id="main">
          <AccountSwitcher me={me} onSwitch={() => undefined} onCreate={async (name) => { const a = await api.createAccount(name); setMe(await api.switchAccount(a.id)); }} />
        </main>
      </div>
    );
  }

  // An invitation opened by someone who is already signed in (and so cannot be "new").
  const inviteRoute = parseAuthRoute(location.hash);
  if (inviteRoute?.name === "invite") {
    return (
      <AcceptInviteScreen
        api={api}
        token={inviteRoute.token}
        me={me}
        onJoined={(m) => {
          session.setAccount(m.activeAccountId);
          setMe(m);
          location.hash = "#/";
        }}
      />
    );
  }

  // A share invitation opened by someone who is already signed in.
  if (inviteRoute?.name === "share") {
    return <AcceptShareScreen api={api} token={inviteRoute.token} me={me} onJoined={(agentId) => (location.hash = `#/agents/${agentId}`)} />;
  }

  const showOnboarding = route.name === "agents" && agentCount === 0;
  return (
    <div className="app">
      <a className="skip" href="#main" onClick={(e) => { e.preventDefault(); document.getElementById("main")?.focus(); }}>
        Skip to content
      </a>
      <header className="topbar">
        <a href="#/" className="brand">
          Nemo
        </a>
        <MainNav route={route} alerts={alerts} team={{ team: t("nav.team"), account: t("nav.account"), keys: role !== "member", billing: role === "owner" ? t("nav.billing") : undefined }} />
        <AccountSwitcher
          me={me}
          onSwitch={(id) => void switchTo(id)}
          onCreate={async (name) => {
            const a = await api.createAccount(name);
            await switchTo(a.id);
          }}
        />
        <button className="btn ghost" onClick={signOut}>
          {t("nav.signout")}
        </button>
      </header>
      {me.mfa.nudge && route.name !== "account" && (
        <div className="banner ok nudge" role="region" aria-label={t("mfa.security")}>
          <p>{me.mfa.nudge === "trial_end" ? t("mfa.nudgeTrial") : t("mfa.nudgeFirst")}</p>
          <div className="row">
            <a className="btn primary" href="#/account?mfa=1">
              {t("mfa.nudgeSetup")}
            </a>
            <button className="btn ghost" onClick={() => void api.mfaDismissNudge().then(() => api.me()).then(setMe, () => undefined)}>
              {t("mfa.nudgeLater")}
            </button>
          </div>
        </div>
      )}
      {billing && route.name !== "billing" && (
        <div className="page-banner">
          <BillingBanner info={billing} />
        </div>
      )}
      {showOnboarding ? (
        <Onboarding api={api} me={me} onOpen={(id) => (location.hash = `#/agents/${id}`)} />
      ) : route.name === "team" ? (
        <Team api={api} me={me} accountName={active.name} onLeft={() => void api.me().then(setMe, onSignedOut).then(() => (location.hash = "#/"))} />
      ) : route.name === "share" ? (
        <AgentSharing key={route.id} api={api} me={me} id={route.id} onLeft={() => (location.hash = "#/")} />
      ) : route.name === "transfer" ? (
        <AcceptTransferScreen
          api={api}
          me={me}
          token={route.token}
          id={route.id}
          onDone={(fresh) => {
            session.setAccount(fresh.activeAccountId);
            setMe(fresh);
          }}
        />
      ) : route.name === "account" ? (
        <AccountPage api={api} me={me} onChanged={setMe} onSignedOut={onSignedOut} onDeleted={() => void api.me().then(setMe, onSignedOut).then(() => (location.hash = "#/"))} />
      ) : route.name === "billing" ? (
        role === "owner" ? <Billing key={me.activeAccountId} api={api} intent={route.intent} /> : <main className="page" id="main"><div className="banner warn" role="alert">{t("billing.ownerOnly")}</div></main>
      ) : route.name === "keys" && role === "member" ? (
        <main className="page" id="main"><div className="banner warn" role="alert">{t("keys.forbidden")}</div></main>
      ) : (
        <Routes api={api} route={route} models={models} keysNote={t("keys.note")} />
      )}
    </div>
  );
}

function AgentScreen({ api, id, step, models }: { api: Api; id: string; step: StepId; models: ModelRegistry | null }) {
  const { t } = useI18n();
  const [detail, setDetail] = useState<AgentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.getAgent(id).then(setDetail, (e) => setError(e instanceof ApiError ? e.message : "Could not load this agent."));
  }, [api, id]);
  if (error) {
    return (
      <main className="page">
        <div className="banner warn" role="alert">{error}</div>
        <a href="#/">← All agents</a>
      </main>
    );
  }
  if (!detail) return <main className="page"><p>Loading…</p></main>;
  const viewer = (detail.shared && detail.role === "viewer") || !!detail.planBlock;
  return (
    <>
      <div className="row between" style={{ padding: "0 1rem" }}>
        {detail.shared ? <span className="badge info">Shared</span> : <span />}
        <a className="btn secondary" href={`#/agents/${id}/share`}>
          Share
        </a>
      </div>
      {detail.planBlock ? (
        <div className="banner warn" role="status">
          <p>{detail.planBlock.message}</p>
          {detail.planBlock.upgradeUrl && (
            <a className="btn primary" href={detail.planBlock.upgradeUrl}>
              {t("plan.upgrade")}
            </a>
          )}
        </div>
      ) : (
        viewer && <div className="banner warn" role="status">{t("share.readOnly")}</div>
      )}
      {!detail.shared && <ModelMigrationBanner api={api} agentId={id} />}
      <fieldset disabled={viewer} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <Editor api={api} initial={detail} models={models} step={step} onStep={(s) => (location.hash = `#/agents/${id}?step=${s}`)} />
      </fieldset>
    </>
  );
}
